import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import useAuth from '@modules/auth/hooks/api/useAuth';
import Routes from '@common/defs/routes';
import AuthRoutes from '@modules/auth/defs/routes';
import ApiRoutes from '@common/defs/api-routes';
import { NextPageWithLayout } from '../../../pages/_app';

type Props = Record<string, unknown>;

export enum AUTH_MODE {
  LOGGED_IN = 'LOGGED_IN',
  LOGGED_OUT = 'LOGGED_OUT',
}

interface WithAuthOptions {
  mode?: AUTH_MODE;
  redirectUrl?: string;
}

const withAuth = (Component: NextPageWithLayout<Props>, options: WithAuthOptions = {}) => {
  const WrappedComponent = (props: Props) => {
    const authEnabled = process.env.NEXT_PUBLIC_AUTH_ENABLED === 'true';
    const router = useRouter();
    const { user } = useAuth();
    const [isClient, setIsClient] = React.useState(false);
    const mode = options.mode ?? AUTH_MODE.LOGGED_IN;

    useEffect(() => {
      setIsClient(true);
    }, []);

    if (!authEnabled) {
      return <Component {...(props as Props)} />;
    }

    if (!isClient) {
      return null;
    }
    if (!router.isReady) {
      return null;
    }
    if (mode === AUTH_MODE.LOGGED_IN && !user) {
      let url = router.pathname;
      Object.keys(router.query).forEach((k) => {
        url = url.replace(`[${k}]`, router.query[k] as string);
      });
      router.push({
        pathname: options.redirectUrl ? options.redirectUrl : Routes.Auth.Login,
        query: {
          ...router.query,
          url: encodeURIComponent(url),
        },
      });
      return null;
    }

    if (user && user.registrationStep && user.registrationStep !== 'completed') {
      if (
        router.pathname !== AuthRoutes.CompleteRegistration &&
        router.pathname !== ApiRoutes.Auth.Social &&
        router.pathname !== '/auth'
      ) {
        router.replace(AuthRoutes.CompleteRegistration);
        return null;
      }
    }

    if (mode === AUTH_MODE.LOGGED_OUT && user) {
      if (user.registrationStep && user.registrationStep !== 'completed') {
        router.replace(AuthRoutes.CompleteRegistration);
        return null;
      }

      const rawRedirect = (router.query.url || router.query.redirect) as string | undefined;
      if (rawRedirect) {
        const returnUrl = decodeURIComponent(rawRedirect);
        const lid = router.query.lid as string | undefined;
        router.push(
          lid && !returnUrl.includes('lid=')
            ? `${returnUrl}${returnUrl.includes('?') ? '&' : '?'}lid=${lid}`
            : returnUrl
        );
        return null;
      }

      // Si lid est présent, rediriger vers /prospecter?lid=X
      if (router.query.lid) {
        router.push(`/prospecter?lid=${router.query.lid}`);
        return null;
      }

      router.push(options.redirectUrl ?? Routes.Common.Home);
      return null;
    }

    return <Component {...(props as Props)} />;
  };

  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name})`;
  WrappedComponent.getLayout = Component.getLayout;

  return WrappedComponent;
};

export default withAuth;
